import { ReactNode, memo, useMemo } from 'react';
import { useAuth } from 'src/features/auth/contexts/AuthContext';
import { useOrg } from 'src/shared/contexts/OrgContext';
import { Role } from 'src/shared/types/organisation.types';

interface WithAccessControlProps {
  children: ReactNode;
  allowedOrgs?: string[];
  disallowedOrgs?: string[];
  allowedUsers?: string[];
  allowedRoles?: Role[];
  allowInternal?: boolean;
  fallback?: ReactNode;
  blockSupport?: boolean;
  blockReaders?: boolean;
  renderKey?: unknown;
  blockOutlookAppOnlyRole?: boolean;
}

function WithAccessControl({
  children,
  allowedRoles,
  allowedOrgs,
  allowedUsers,
  disallowedOrgs,
  allowInternal,
  blockSupport,
  blockReaders,
  blockOutlookAppOnlyRole,
  fallback,
}: WithAccessControlProps) {
  const { user } = useAuth();
  const { role, currentOrg } = useOrg();

  // Precompute access decision to minimize render work
  const hasAccess = useMemo(() => {
    return isAccessAllowed({
      role,
      userSub: user?.sub,
      orgId: currentOrg.id,
      orgType: currentOrg.orgType,
      allowedRoles,
      allowedUsers,
      allowedOrgs,
      disallowedOrgs,
      allowInternal,
      blockSupport,
      blockReaders,
      blockOutlookAppOnlyRole,
    });
  }, [
    role,
    user?.sub,
    currentOrg.id,
    currentOrg.orgType,
    allowedRoles,
    allowedUsers,
    allowedOrgs,
    disallowedOrgs,
    allowInternal,
    blockSupport,
    blockReaders,
    blockOutlookAppOnlyRole,
  ]);

  return hasAccess ? <>{children}</> : <>{fallback}</>;
}

export type AccessControlRule = {
  allowedOrgs?: string[];
  disallowedOrgs?: string[];
  allowedUsers?: string[];
  allowedRoles?: Role[];
  allowInternal?: boolean;
  blockSupport?: boolean;
  blockReaders?: boolean;
  blockOutlookAppOnlyRole?: boolean;
};

export function isAccessAllowed({
  role,
  userSub,
  orgId,
  orgType,
  allowedRoles,
  allowedUsers,
  allowedOrgs,
  disallowedOrgs,
  allowInternal,
  blockSupport,
  blockReaders,
  blockOutlookAppOnlyRole,
}: {
  role: Role;
  userSub?: string;
  orgId: string;
  orgType: string;
} & AccessControlRule) {
  if (!blockSupport && role === 'SUPPORT') return true;
  if (allowedRoles && allowedRoles.includes(role)) return true;
  if (allowedUsers && allowedUsers.includes(userSub)) return true;
  if (allowedOrgs && allowedOrgs.includes(orgId)) return true;
  if (disallowedOrgs && !disallowedOrgs.includes(orgId)) return true;
  if (allowInternal && orgType === 'INTERNAL') return true;
  if (blockReaders && role !== 'READER') return true;
  if (blockOutlookAppOnlyRole && role !== 'OUTLOOK_APP_ONLY') return true;
  return false;
}

const MemoWithAccessControl = memo(WithAccessControl);

export default MemoWithAccessControl;
